/**
 * `/invite/:token` — accept an organization invitation (#1152: with proof of the invited address when this account
 * hasn't proven it). Its own module so its behaviour is tested rendered (`invite-accept-page.test.tsx`).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { classifyProofCode, PROOF_CODE_LENGTH, proofRefusalKind, typedCodeSurvivesFailedSend } from '@papan/types'
import { useAuth } from '../hooks/useAuth'

export function InviteAcceptPage() {
  const { isAuthenticated, isLoading, user } = useAuth()
  const { settings } = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  // The accept is POSTed once per (TOKEN, ACCOUNT) — guarded on those values, per CLAUDE.md's StrictMode rule:
  // StrictMode's double effect, an auth refetch, a language switch or an unstable `navigate` must never re-send it (a
  // re-run after a successful accept would answer 'Invalid or expired invitation' over the success). A different
  // signed-in account on the same page is a new attempt — its answer may differ.
  const acceptedFor = useRef<string | null>(null)
  const { token } = useParams<{ token: string }>()
  const [status, setStatus] = useState<'loading' | 'accepted' | 'error' | 'needs-code'>('loading')
  const [error, setError] = useState('')
  const [_orgName, setOrgName] = useState('')
  // #1152: the account hasn't proven the invited address — a code sent TO it must come first.
  const [proofEmail, setProofEmail] = useState('')
  const [code, setCode] = useState('')
  // The typed code, for sendCode without making it a dependency (it would be rebuilt on every keystroke). Updated after
  // commit, never during render (a discarded render must not leave an uncommitted value).
  const codeRef = useRef(code)
  // One code action at a time per (invitation, ACCOUNT) — the attempt key (`acceptedFor`). Another account on the page
  // is a different key, so it is never blocked by this one's pending request. Coming BACK to an account (A -> B -> A)
  // is the same key on purpose: A's earlier send or confirm is still A's, its answer still applies (as #1205's
  // `still()` does), and a second action for A waits for it — at most the request timeout (30 s) — instead of racing
  // it (two sends for one account replace each other's code; a confirm racing a plain accept answers out of order).
  // One entry PER attempt: a single slot was overwritten by B's action, so coming back to A no longer saw A's
  // still-pending one (fresh review of #1251).
  const actionsInFlight = useRef(new Map<string | null, object>())
  useLayoutEffect(() => { codeRef.current = code })
  const [codeNote, setCodeNote] = useState('')
  const [busy, setBusy] = useState<'' | 'sending' | 'confirming'>('')

  const finishAccepted = useCallback((tenantId: string) => {
    setOrgName(tenantId) // We don't have the name, but the redirect will show it
    setStatus('accepted')
    setTimeout(() => navigate(`/t/${tenantId}/folder`), 1500)
  }, [navigate])

  const sendCode = useCallback(async () => {
    // One action at a time, by REF: two clicks or submits in one tick both see the render's `busy` as idle (#1217).
    const mine = acceptedFor.current
    if (!token || actionsInFlight.current.has(mine)) return
    const action = {}
    actionsInFlight.current.set(mine, action)
    // Every answer below belongs to the attempt that asked: one that lands after another account's attempt began
    // is dropped (never a 'code sent' for a code bound to the previous account, nor its accept).
    const still = () => acceptedFor.current === mine
    setBusy('sending')
    setError('')
    // A new code replaces the old one: clear what's typed NOW (not when the answer lands, which could wipe a new code
    // pasted meanwhile) — Confirm on the replaced code would only spend a guess (#1217). If the send fails and the typed
    // code is still live (`typedCodeSurvivesFailedSend`), it is put back below, unless something new was typed.
    const cleared = codeRef.current
    setCode('')
    try {
      const { orgsApi } = await import('@papan/orgs/client')
      // The survive rule judges the SEND's own failure only — not the accept below.
      const r = await orgsApi.sendInviteCode(token).catch((err: unknown) => {
        if (still() && typedCodeSurvivesFailedSend(err)) setCode((c) => (c === '' ? cleared : c))
        throw err
      })
      if (!still()) return
      if (r.alreadyProven) {
        // Proven meanwhile (e.g. added in Settings, or accepted in another tab) — the plain accept goes through now.
        const result = await orgsApi.acceptInviteByToken(token)
        if (!still()) return
        finishAccepted(result.tenantId)
        return
      }
      if (r.email) setProofEmail(r.email)
      setCodeSent(true)
      setCodeNote(t('inviteAccept.sent', { email: r.email ?? proofEmail }))
    } catch (err: any) {
      if (still()) {
        // A proof-code refusal in the person's language (#1217); the server's sentence is English.
        const kind = proofRefusalKind(err)
        setError(kind ? t(`proofCode.${kind}`, { length: PROOF_CODE_LENGTH }) : err?.message || t('inviteAccept.sendFailed'))
      }
    } finally {
      if (actionsInFlight.current.get(mine) === action) actionsInFlight.current.delete(mine)
      if (still()) setBusy('')
    }
  }, [token, proofEmail, t, finishAccepted])

  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/invite/${token}`)
      return
    }
    const attemptKey = `${token}:${user?.id ?? ''}`
    if (!token || acceptedFor.current === attemptKey) return
    acceptedFor.current = attemptKey
    // A new attempt (another account on this page) starts clean — nothing of the previous account's state carries
    // over — and an answer that arrives after a newer attempt began is ignored.
    const current = () => acceptedFor.current === attemptKey
    setStatus('loading'); setError(''); setCodeSent(false); setCode(''); setCodeNote(''); setProofEmail(''); setBusy('')

    // Accept the invite
    import('@papan/orgs/client').then(async ({ orgsApi }) => {
      try {
        const result = await orgsApi.acceptInviteByToken(token)
        if (!current()) return
        finishAccepted(result.tenantId)
      } catch (err: any) {
        if (!current()) return
        if (err?.code === 'INVITE_ADDRESS_PROOF_REQUIRED') {
          // The account hasn't proven the invited address: show which address, and offer to send a code to it.
          if (typeof err?.email === 'string') setProofEmail(err.email)
          setStatus('needs-code')
          return
        }
        setError(err?.message || t('inviteAccept.acceptFailed'))
        setStatus('error')
      }
    })
  }, [isAuthenticated, isLoading, token, navigate, finishAccepted, t, user?.id])

  // #1152 (code-review round 1): no code is sent on load — only when the person asks. Opening a forwarded link, or
  // reloading the page, must not email the invited address (or spend the send cap).
  const [codeSent, setCodeSent] = useState(false)

  // As the server reads it (the ONE rule, `classifyProofCode`): Confirm only for a code that could match — a half-typed
  // or misread one must not spend one of the code's attempts — and a hint for one that never can.
  const sent = classifyProofCode(code)
  const codeComplete = sent.kind === 'ok'
  const misread = sent.kind === 'malformed'   // never while a valid start is still being typed ('incomplete')
  const confirmCode = async () => {
    const mine = acceptedFor.current
    if (!token || sent.kind !== 'ok' || actionsInFlight.current.has(mine)) return
    const action = {}
    actionsInFlight.current.set(mine, action)
    const still = () => acceptedFor.current === mine   // as in sendCode: a late answer to another attempt is dropped
    setBusy('confirming')
    setError('')
    try {
      const { orgsApi } = await import('@papan/orgs/client')
      const result = await orgsApi.acceptInviteByToken(token, sent.code)
      if (!still()) return
      finishAccepted(result.tenantId)
    } catch (err: any) {
      if (still()) {
        const kind = proofRefusalKind(err)   // as in sendCode
        setError(kind ? t(`proofCode.${kind}`, { length: PROOF_CODE_LENGTH }) : err?.message || t('inviteAccept.acceptFailed'))
      }
    } finally {
      if (actionsInFlight.current.get(mine) === action) actionsInFlight.current.delete(mine)
      if (still()) setBusy('')
    }
  }

  // Theme class applied here, not inherited: this route renders directly under
  // the router with no `ThemedArea` above it, so without it every `themed-*`
  // class below resolves to an undefined variable — black text on an unpainted
  // background, the same defect the auth gate had.
  return (
    <div className={`${getThemeClass(settings.theme.workspace)} h-screen flex items-center justify-center themed-bg-primary`}>
      <div className="text-center space-y-3 max-w-sm px-4">
        {status === 'loading' && <p className="themed-text">{t('inviteAccept.accepting')}</p>}
        {status === 'accepted' && <p className="text-green-400">{t('inviteAccept.accepted')}</p>}
        {status === 'needs-code' && (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void confirmCode() }}>
            <h1 className="themed-text text-lg font-medium">{t('inviteAccept.proofTitle')}</h1>
            <p className="themed-text-secondary text-sm">
              {codeSent
                ? (proofEmail ? t('inviteAccept.proofBody', { email: proofEmail }) : t('inviteAccept.proofBodyNoAddress'))
                : proofEmail ? t('inviteAccept.proofBodyBeforeSendTo', { email: proofEmail }) : t('inviteAccept.proofBodyBeforeSend')}
            </p>
            {!codeSent && (
              <button type="button" onClick={() => void sendCode()} disabled={busy !== ''} className="w-full py-2 rounded bg-blue-600 text-white disabled:opacity-50">
                {busy === 'sending' ? t('proofCode.sending') : t('inviteAccept.send')}
              </button>
            )}
            {/* A code already in the inbox (the tab reloaded, or it was sent from another device) — enter it without
                sending a new one, which would replace it. */}
            {!codeSent && (
              <button type="button" onClick={() => setCodeSent(true)} className="text-sm text-blue-400 hover:text-blue-300">
                {t('inviteAccept.haveCode')}
              </button>
            )}
            {codeSent && <>
            <label className="block text-left text-sm themed-text" htmlFor="invite-code">{t('inviteAccept.codeLabel')}</label>
            <input
              id="invite-code"
              inputMode="numeric"
              spellCheck={false}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('proofCode.placeholder', { length: PROOF_CODE_LENGTH })}
              className="w-full px-3 py-2 rounded border themed-border themed-bg-secondary themed-text text-center tracking-widest"
            />
            <button type="submit" disabled={!codeComplete || busy !== ''} className="w-full py-2 rounded bg-blue-600 text-white disabled:opacity-50">
              {busy === 'confirming' ? t('inviteAccept.confirming') : t('inviteAccept.confirm')}
            </button>
            <button type="button" onClick={() => void sendCode()} disabled={busy !== ''} className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50">
              {busy === 'sending' ? t('proofCode.sending') : t('proofCode.resend')}
            </button>
            </>}
            {misread && <p className="text-xs text-amber-400">{t('proofCode.malformed', { length: PROOF_CODE_LENGTH })}</p>}
            {codeNote && !error && <p className="text-xs themed-text-secondary">{codeNote}</p>}
            {error && <p className="text-sm text-red-400">{error}</p>}
            {proofEmail && <p className="text-xs themed-text-secondary">{t('inviteAccept.wrongAccount', { email: proofEmail })}</p>}
          </form>
        )}
        {status === 'error' && (
          <>
            <p className="text-red-400">{error}</p>
            <button onClick={() => navigate('/~/folder')} className="text-sm text-blue-400 hover:text-blue-300">
              {t('inviteAccept.goToDrive')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
